import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {LabelingStrategy, LABELING_STRATEGIES_REQUIRING_NAME, TagSchema} from 'constants/RegistryConstants';
import {ComplexPolymerCreateStep} from 'modbio/complex-polymers/create/CreateComplexPolymerModal';
import CreateBioentityInputs from 'registry/create-bioentity/CreateBioentityInputs';
import * as CreateBioentityUtil from 'registry/create-bioentity/CreateBioentityUtil';
import {
  CreateAndRegisterBioentityArgs,
  CreateBioentityArgs,
} from 'registry/create-bioentity/CreateBioentityUtil';
import SchemaLabelingStrategyInput from 'registry/labeling-strategies/SchemaLabelingStrategyInput';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import FormGroup from 'ui-library/bs/FormGroup';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';

import {CreateAndRegisterStep} from './CreateAndRegisterBioentityModal';

export interface CreateAndRegisterBioentityInputsProps {
  createAndRegisterBioentityArgs: CreateAndRegisterBioentityArgs;
  isOligoSchema?: boolean;
  labelStyle: LabelStyle;
  onChange: (createAndRegisterBioentityArgs: Partial<CreateAndRegisterBioentityArgs>) => void;
  showSchemaSelector: boolean;
  showStudiesField: boolean;
  isTagSchemaDisabledFn?: (schema: TagSchema) => boolean;
  tagSchemaDisabledMessage?: string;

  /**
   * createStep/setCreateStep are used to manage create flows that need to override the entire input UI at certain stages.
   * (e.g., if the user clicks into a link to configure some more detailed aspect of the entity)
   */
  createStep?: CreateAndRegisterStep | null;
  setCreateStep?: (createStep: CreateAndRegisterStep | null) => void;
}

export default class CreateAndRegisterBioentityInputs extends React.Component<CreateAndRegisterBioentityInputsProps> {
  _handleChange(update: Partial<CreateAndRegisterBioentityArgs>): void {
    this.props.onChange(update);
  }

  _handleChangeTagSchema = (tagSchema: TagSchema | null): void => {
    assert(tagSchema != null, 'TagSchemaSelector returned null for the tagSchema');
    this._handleChange({tagSchema});
  };

  _handleChangeLabelingStrategy = (labelingStrategy: LabelingStrategy): void => {
    this._handleChange({labelingStrategy});
  };

  _handleChangeCreateBioentityInputArgs = (createArgs: Partial<CreateBioentityArgs>): void => {
    this._handleChange(CreateBioentityUtil.toCreateAndRegisterBioentityArgs(createArgs));
  };

  override render(): JSX.Element {
    const shouldOnlyShowAttachmentsMenu =
      this.props.createStep === ComplexPolymerCreateStep.CONFIGURE_ATTACHMENTS;

    const {
      createAndRegisterBioentityArgs: {labelingStrategy, tagSchema},
      isOligoSchema,
    } = this.props;
    const shouldShowNameField = Boolean(
      labelingStrategy && LABELING_STRATEGIES_REQUIRING_NAME.includes(labelingStrategy)
    );

    const schemaInputs = (
      <>
        {this.props.showSchemaSelector && (
          <FormGroup>
            <FieldLabel label="Schema" labelStyle={this.props.labelStyle} isRequired={true} />
            <TagSchemaSelector
              onChange={this._handleChangeTagSchema}
              selectedTagSchemaId={tagSchema ? tagSchema.id : null}
              isTagSchemaDisabledFn={this.props.isTagSchemaDisabledFn}
              disabledMessage={this.props.tagSchemaDisabledMessage}
              requiredPermissions={['objectsCreatable', 'objectsRegisterable']}
            />
          </FormGroup>
        )}

        <FormGroup>
          <SchemaLabelingStrategyInput
            labelingStrategy={labelingStrategy}
            labelStyle={this.props.labelStyle}
            onChange={this._handleChangeLabelingStrategy}
            tagSchemaId={tagSchema ? tagSchema.id : null}
          />
        </FormGroup>
      </>
    );

    return (
      <div
        className={
          shouldOnlyShowAttachmentsMenu ? css(styles.wrapperComplexPolymerAttachments) : css(styles.wrapper)
        }
      >
        {!shouldOnlyShowAttachmentsMenu && schemaInputs}

        <CreateBioentityInputs
          createBioentityArgs={CreateBioentityUtil.toCreateBioentityArgs(
            this.props.createAndRegisterBioentityArgs
          )}
          onChange={this._handleChangeCreateBioentityInputArgs}
          labelStyle={LabelStyle.LABEL}
          isOligoSchema={Boolean(isOligoSchema)}
          shouldShowNameField={shouldShowNameField}
          showStudiesField={this.props.showStudiesField}
          isProjectFolderRequired={BenchlingConfig.ENABLE_PROJECTS_REQUIRED_FOR_REGISTRY_FILES}
          createStep={this.props.createStep}
          setCreateStep={this.props.setCreateStep}
        />
      </div>
    );
  }
}

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  wrapperComplexPolymerAttachments: {
    height: '90%',
    display: 'flex',
    flexDirection: 'column',
  },
});
